/** @jsx jsx */
import React, { memo, useCallback, type MouseEvent } from 'react';
import { jsx, styled as styled2 } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { HighlightText, shouldHighlightText } from '../highlight-text/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const CardKey = memo(
	({
		text,
		highlight = [],
		onClick,
		isDone = false,
		stopEventPropagation = true,
		openInNewTab = false,
		renderAsText = false,
	}: Props) => {
		const { formatMessage } = useIntl();
		const highlightedText =
			highlight && shouldHighlightText(text, highlight) ? (
				<HighlightText phrases={highlight} content={text} />
			) : (
				text
			);

		const onClickKey = useCallback(
			(event: React.MouseEvent<HTMLAnchorElement>) => {
				const isModifierClick = event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;
				stopEventPropagation && event.stopPropagation();

				if (!isModifierClick && !openInNewTab) {
					event.preventDefault();
					onClick?.(event);
				}
			},
			[onClick, openInNewTab, stopEventPropagation],
		);

		// prevent the context menu and Bento from opening
		const onContextMenu = useCallback((event: MouseEvent) => event.stopPropagation(), []);

		return (
			<Container data-testid="platform-card.common.ui.key.key">
				<Tooltip content={text}>
					<StyledLink
						isLineThrough={Boolean(isDone)}
						{...(!renderAsText && { href: `/browse/${text}`, target: '_blank' })}
						onContextMenu={onContextMenu}
						onClick={onClickKey}
						{...(Boolean(isDone) && {
							'aria-label': formatMessage(messages.label, {
								issueKey: text,
							}),
						})}
					>
						<Box xcss={[KeyStyles, isVisualRefreshEnabled() && KeyRefreshStyles]}>
							{highlightedText}
						</Box>
					</StyledLink>
				</Tooltip>
			</Container>
		);
	},
);

export default CardKey;

type StyledLinkProps = { isLineThrough: boolean };
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLink = styled2.a<StyledLinkProps>({
	userSelect: 'initial',
	color: token('color.text.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textDecoration: (props) => (props.isLineThrough ? 'line-through' : 'none'),
	'&:focus, &:hover, &:visited, &:link, &:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		textDecoration: (props) => (props.isLineThrough ? 'line-through' : 'none'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Container = styled2.div({
	flex: '0 1 auto',
	direction: 'rtl',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	'&:focus-within': {
		outline: `${token('border.width.outline')} auto
${''}
            ${token('color.border.focused')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Key = styled2.span({
	display: 'block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.semibold'),

	color: token('color.text.subtle'),
});

const KeyStyles = xcss({
	display: 'block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: 'font.body.UNSAFE_small',
	fontWeight: token('font.weight.semibold'),
	color: 'color.text.subtle',
});

const KeyRefreshStyles = xcss({
	fontWeight: token('font.weight.medium'),
});
